import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Select,
  MenuItem,
  Chip,
  Paper,
  Grid,
  Typography,
  Checkbox,
  TextField,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  LinearProgress,
  Switch
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import { useUser } from '../../contexts/UserContext';
import { getAvailableLocations } from '../../hooks/unitDataHooks';
import { useModel } from '../../contexts/ModelContext';
import { doc, updateDoc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { ModelProvider } from '../../contexts/ModelContext';
import DeploymentConfigSubsection from './DeploymentConfig/DeploymentConfigSubsection';
import DeploymentConfig from './DeploymentConfig/DeploymentConfig';
import DeploymentConfigFraction from './DeploymentConfig/DeploymentConfigFraction';
import Dropdown from '../../components/Dropdown/Dropdown';
import UnitDeploymentConfig from './DeploymentConfig/UnitDeploymentConfig';
import DeploymentConfigSectionCheckedState from './DeploymentConfig/DeploymentConfigSectionCheckedState';
import ConsistencyDialogOption from './DeploymentConfig/ConsistencyDialogOption';
import Toggle from '../../components/Toggle/Toggle';
import './RollOutPage.css';

const RollOutPage = () => {
  const [deploymentConfigText, setDeploymentConfigText] = useState('');
  const [metadata, setMetadata] = useState({});
  const [consistencyDialogSelectedOptionIndex, setConsistencyDialogSelectedOptionIndex] = useState(null);
  const [consistencyDialogOptions, setConsistencyDialogOptions] = useState([]);
  const [dropdownTestValue, setDropdownTestValue] = useState('');
  const { userData } = useUser();
  const { models, loading: modelsLoading, modelConfig, fetchModelConfig, fetchModelConfigByUnit } = useModel();
  const [units, setUnits] = useState([]); // Array of { id, label, deploymentConfig }
  const [displayUnits, setDisplayUnits] = useState([]); // Array of { id, label, locationName, index, displayUnit, deploymentConfig }
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [category, setCategory] = useState('wood');
  const [fractions, setFractions] = useState([
    { key: 'normal_wood', displayName: 'Indenders træ', image: 'wood.png', arrow: 'none' }
  ]);
  const [upperLine, setUpperLine] = useState('Placer træ');
  const [lowerLine, setLowerLine] = useState('Tryk på knap');
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Add state for section checkboxes
  const [checkedSections, setCheckedSections] = useState({
    model: false,
    classes: false,
    mapping: false,
    display: false,
    fraction_displayName: false,
    fractionIcon: false,
    fractionArrow: false
  });

  // Add new state for current values and section states
  const [currentValues, setCurrentValues] = useState({
    category: '',
    model: '',
    fractions: [],
    mappings: {},
    display: {
      upperLine: '',
      lowerLine: ''
    }
  });

  const [sectionStates, setSectionStates] = useState({
    model: { hasValue: false, isConsistent: true },
    fractions: { hasValue: false, isConsistent: true },
    mappings: { hasValue: false, isConsistent: true },
    display: { hasValue: false, isConsistent: true }
  });

  // Add new state for dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSection, setDialogSection] = useState(null);
  const [dialogValues, setDialogValues] = useState([]);
  const [selectedValue, setSelectedValue] = useState(null);
  const [selectedUnits, setSelectedUnits] = useState([]);

  // Add new state for tracking last known mapping state
  const [lastKnownMappingState, setLastKnownMappingState] = useState(false);
  const [lastKnownFractionStates, setLastKnownFractionStates] = useState({
    displayName: false,
    icon: false,
    arrow: false
  });

  // Initialize mappings with dynamic class names
  const [mappings, setMappings] = useState([]);

  // Add new state for progress
  const [progress, setProgress] = useState({
    current: 0,
    total: 0,
    message: '',
    isProcessing: false
  });

  const initialDeploymentConfigSubsections = [
    new DeploymentConfigSubsection(
      "useExposureAlgorithm",
      false,
      'boolean',
      false,
      "Use exposure algorithm"
    ),
  ]

  const [deploymentConfigSubsections, setDeploymentConfigSubsections] = useState(initialDeploymentConfigSubsections);

  const initialDeploymentConfig = new DeploymentConfig();
  const [deploymentConfig, setDeploymentConfig] = useState(initialDeploymentConfig);
  const [selectedTargetsDeploymentConfigs, setSelectedTargetsDeploymentConfigs] = useState([]);
  const [sectionCheckedStateForSection, setSectionCheckedStateForSection] = useState({})

  const loadUnits = async () => {
    try {
      // Load regular units from the "units" collection
      const unitsCollection = collection(db, 'units');
      const unitsQuerySnapshot = await getDocs(unitsCollection);
      const allUnits = [];
      
      unitsQuerySnapshot.forEach(doc => {
        const unitData = doc.data();
        allUnits.push({
          id: doc.id,
          label: unitData.name || doc.id,
          deploymentConfig: unitData.deployment_config || {}
        });
      });
      
      setUnits(allUnits);
      
      // Load display units from locations
      if (userData && userData.companies) {
        const locations = await getAvailableLocations(userData);
        const allDisplayUnits = [];
        
        // Create a map of location ID to company ID
        const locationToCompanyMap = {};
        for (const companyId of userData.companies) {
          const locationsRef = collection(db, `companies/${companyId}/locations`);
          const locationDocs = await getDocs(locationsRef);
          locationDocs.forEach(doc => {
            locationToCompanyMap[doc.id] = companyId;
          });
        }
        
        // Extract display_units from each location
        locations.forEach(location => {
          if (location.display_units && Array.isArray(location.display_units)) {
            location.display_units.forEach((displayUnit, index) => {
              const description = displayUnit.description || 'no_description';
              const label = `${location.name} - ${index} - ${description}`;
              allDisplayUnits.push({
                id: `${location.id}_${index}`,
                label,
                locationName: location.name,
                locationId: location.id,
                companyId: locationToCompanyMap[location.id],
                index,
                displayUnit,
                deploymentConfig: displayUnit.deployment_config || {}
              });
            });
          }
        });
        
        setDisplayUnits(allDisplayUnits);
      }
    } catch (error) {
      console.error("Error fetching units and display units: ", error);
    } finally {
      setLoading(false);
    }
  };

  // Update mappings when classNames changes
  useEffect(() => {
    if (modelConfig.cls_names.length > 0) {
      setMappings(modelConfig.cls_names.map(name => ({ source: name, target: 'normal_wood' })));
    }
  }, [modelConfig.cls_names]);

  // Load locations and display units from Firestore
  useEffect(() => {
    loadUnits();
  }, [userData]);

  const handleUpdateDeploymentConfigSubsections = (index, field, value) => {
    const updatedSubsections = [...deploymentConfigSubsections];
    updatedSubsections[index] = {
      ...updatedSubsections[index],
      [field]: value
    };
    setDeploymentConfigSubsections(updatedSubsections);
  };

  // Function to get available display units
  const getAvailableUnits = () => {
    return displayUnits;
  };

  // List of class names for mapping
  const class_names = [
    'wood_painted',
    'wood_unpainted',
    'wood_old',
    'wood_new',
    'wood_damaged',
    'wood_rotten'
  ];

  // Add handler for removing targets
  const handleRemoveTarget = (targetToRemove) => {
    setSelectedTargets(prev => prev.filter(target => target.id !== targetToRemove.id));
  };

  // Add handler for toggling targets
  const handleToggleTarget = async (target) => {
    var selectedTargetsNewValue = [...selectedTargets];
    if (selectedTargetsNewValue.some(t => t.id === target.id)) {
      selectedTargetsNewValue = [];
    } else {
      selectedTargetsNewValue = [target];
    }
    setSelectedTargets(prev => {
      return selectedTargetsNewValue
    });
  };

  // Add handler for selecting all targets
  const handleSelectAllTargets = () => {
    setSelectedTargets(prev => {
      const availableUnits = getAvailableUnits();
      const selectedAvailableUnits = prev.filter(unit => availableUnits.some(av => av.id === unit.id));
      
      // If all available units are already selected, deselect all available units
      if (selectedAvailableUnits.length === availableUnits.length) {
        return prev.filter(unit => !availableUnits.some(av => av.id === unit.id));
      }
      
      // Otherwise, select all available units while preserving selections of unavailable units
      const unavailableSelectedUnits = prev.filter(unit => !availableUnits.some(av => av.id === unit.id));
      return [...unavailableSelectedUnits, ...availableUnits];
    });
  };

  // Add handler for adding new fraction
  const handleAddFraction = () => {
    setDeploymentConfig(prev => ({
      ...prev,
      classes: {
        ...prev.classes,
        fractions: [...prev.classes.fractions, { key: '', displayName: '', image: '', arrow: 'none' }]
      }
    }));
  };

  // Add handler for updating fraction
  const handleUpdateFraction = (index, field, value) => {
    setDeploymentConfig(prev => ({
      ...prev,
      classes: {
        ...prev.classes,
        fractions: prev.classes.fractions.map((f, i) => i === index ? { ...f, [field]: value } : f)
      }
    }));
  };

  // Add handler for updating arrow selection
  const handleArrowSelect = (index, direction) => {
    setDeploymentConfig(prev => ({
      ...prev,
      classes: {
        ...prev.classes,
        fractions: prev.classes.fractions.map((f, i) => i === index ? { ...f, arrow: direction } : f)
      }
    }));
  };

  // Add handler for deleting fraction
  const handleDeleteFraction = (index) => {
    setDeploymentConfig(prev => ({
      ...prev,
      classes: {
        ...prev.classes,
        fractions: prev.classes.fractions.filter((_, i) => i !== index)
      }
    }));
  };

  // Function to deselect units that are not available in the current filter
  const deselectHiddenUnits = () => {
    const availableUnits = getAvailableUnits();
    setSelectedTargets(prev => {
      const newSelectedTargets = prev.filter(unit => availableUnits.some(av => av.id === unit.id));
      return newSelectedTargets;
    });
  };

  // Update handleToggleSection to store fraction states
  const handleToggleSection = (section) => {
    const checkedStateOldValue = sectionCheckedStateForSection[section];
    var checkedStateNewValue;
    switch (checkedStateOldValue) {
      case DeploymentConfigSectionCheckedState.UNCHECKED:
        checkedStateNewValue = DeploymentConfigSectionCheckedState.PROPOSED_CHECKED;
        break;
      case DeploymentConfigSectionCheckedState.CHECKED:
        checkedStateNewValue = DeploymentConfigSectionCheckedState.UNCHECKED;
        break;
      default:
        checkedStateNewValue = DeploymentConfigSectionCheckedState.PROPOSED_CHECKED;
        break;
    }

    if (checkedStateNewValue == DeploymentConfigSectionCheckedState.PROPOSED_CHECKED) {
      var consistencyDialogOptionsNewValue = [];
      const uniqueValues = getUniqueValues(selectedTargets, section);
      for (let i = 0; i < uniqueValues.length; i++) {
        const value = uniqueValues[i];
        const name = formatValueForDisplay(value, section);
        const unitsUsingValue = getUnitsUsingValue(value, section);
        consistencyDialogOptionsNewValue.push(new ConsistencyDialogOption(i, name, unitsUsingValue, value));
      }
      
      setConsistencyDialogOptions(consistencyDialogOptionsNewValue);

      setDialogOpen(true)
    }

    // Special handling for classes section
    if (section === 'classes') {
      const sectionState = sectionStates.fractions;
      if (!sectionState.isConsistent) {
        // If section is inconsistent, show dialog
        const uniqueValues = getUniqueValues(selectedTargets, 'fractions');
        setDialogValues(uniqueValues);
        setDialogSection(section);
        setSelectedValue(uniqueValues[0]); // Default to first value
        setDialogOpen(true);
        return;
      }
    }

    // Special handling for mapping section
    if (section === 'mapping') {
      if (checkedSections.classes) {
        const sectionState = sectionStates.mappings;
        if (!sectionState.isConsistent) {
          // If section is inconsistent, show dialog
          const uniqueValues = getUniqueValues(selectedTargets, 'mappings');
          setDialogValues(uniqueValues);
          setDialogSection(section);
          setSelectedValue(uniqueValues[0]); // Default to first value
          setDialogOpen(true);
          return;
        }

        setLastKnownMappingState(!checkedSections.mapping);
      }

      setSectionCheckedStateForSection(prev => {
        var sectionCheckedStateForSectionNewValue = {...prev};
        sectionCheckedStateForSectionNewValue[section] = checkedStateNewValue;
        return sectionCheckedStateForSectionNewValue;
      });

      return;
    }

    // Handle fraction-specific sections
    if (section.startsWith('fraction')) {
      if (checkedSections.classes) {
        const sectionState = sectionStates.fractions;
        
        if (!sectionState.isConsistent) {
          // If section is inconsistent, show dialog
          const uniqueValues = getUniqueValues(selectedTargets, 'fractions');
          setDialogValues(uniqueValues);
          setDialogSection(section);
          setSelectedValue(uniqueValues[0]); // Default to first value
          setDialogOpen(true);
          return;
        }

        // Store the new state in lastKnownFractionStates
        setLastKnownFractionStates(prev => ({
          ...prev,
          [section.replace('fraction', '').toLowerCase()]: !checkedSections[section]
        }));
      }
      return;
    }

    // Map section names to their corresponding sectionStates keys
    const sectionKey = section === 'classes' ? 'fractions' : 
                      section === 'mapping' ? 'mappings' : 
                      section;

    // Handle main sections
    if (!sectionStates[sectionKey]) {
      console.error(`Section ${sectionKey} not found in sectionStates`);
      return;
    }

    const sectionState = sectionStates[sectionKey];
    
    if (!sectionState.isConsistent) {
      // If section is inconsistent, show dialog
      const uniqueValues = getUniqueValues(selectedTargets, sectionKey);
      setDialogValues(uniqueValues);
      setDialogSection(section);
      setSelectedValue(uniqueValues[0]); // Default to first value
      setDialogOpen(true);
      return;
    }

  };

  function getSectionValue(config, section) {
    var sectionValue
    const fractionPrefix = "fraction_";
    if (section.slice(0, fractionPrefix.length) == fractionPrefix) {
      const fractions = config["classes"]?.fractions;
      const subsection = section.slice(fractionPrefix.length);
      sectionValue = fractions.map(fraction => fraction[subsection]);
    } else {
      sectionValue = config[section]
    }

    return sectionValue;
  }

  // Add function to get unique values for a section
  const getUniqueValues = (units, section) => {
    const values = units.map(unit => {
      const config = unit.deploymentConfig;
      if (config === undefined) {
        return null;
      }

      const sectionValue = getSectionValue(config, section);
      if (sectionValue === undefined) {
        return null;
      }
      return sectionValue;
      // const config = unitData[unit]?.deployment_config;
      // if (!config) return null;
      
      // switch (section) {
      //   case 'model':
      //     return {
      //       category: config.category,
      //       model: config.model
      //     };
      //   case 'fractions':
      //     return config.fractions;
      //   case 'mappings':
      //     return config.mappings;
      //   case 'display':
      //     return config.display;
      //   default:
      //     return null;
      // }
    }).filter(v => v !== null);

    // Remove duplicates by stringifying and comparing
    const uniqueValues = values.filter((value, index, self) => {
      console.log("Section:", section);
      if (section == "fractions") { 
        return index === self.findIndex(v => {
          const equal = areFractionsEqual(v, value);
          return equal;
        });
      } else {
        return index === self.findIndex(v => JSON.stringify(v) === JSON.stringify(value));
      }
    });

    return uniqueValues;
  };

  // Add handler for dialog close
  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogSection(null);
    setDialogValues([]);
    setSelectedValue(null);

    setConsistencyDialogSelectedOptionIndex(null);
  };

  // Add handler for dialog confirm
  const handleDialogConfirm = () => {
    // if (dialogSection) {
    //   setCheckedSections(prev => ({
    //     ...prev,
    //     [dialogSection]: true
    //   }));

    //   if (dialogSection.startsWith('fraction')) {
    //     setLastKnownFractionStates(prev => ({
    //       ...prev,
    //       [dialogSection.replace('fraction', '').toLowerCase()]: true
    //     }));
    //   }

    //   if (dialogSection === 'model') {
    //     setCurrentValues(prev => ({
    //       ...prev,
    //       category: selectedValue.category,
    //       model: selectedValue.model,
    //       model_name: selectedValue.model_name,
    //       model_architecture: selectedValue.model_architecture,
    //       loss_function: selectedValue.loss_function,
    //       inference: selectedValue.inference
    //     }));
    //   } else {
    //     setCurrentValues(prev => ({
    //       ...prev,
    //       [dialogSection]: selectedValue
    //     }));
    //   }

    //   // Update form fields if needed
    //   if (dialogSection === 'fractions') {
    //     setFractions(selectedValue);
    //   } else if (dialogSection === 'mappings') {
    //     const mappingsArray = Object.entries(selectedValue).map(([source, target]) => ({
    //       source,
    //       target
    //     }));
    //     setMappings(mappingsArray);
    //   } else if (dialogSection === 'display') {
    //     setUpperLine(selectedValue.upperLine);
    //     setLowerLine(selectedValue.lowerLine);
    //   }

    //   // Update section states to mark as consistent
    //   setSectionStates(prev => {
    //     const newStates = { ...prev };
    //     const sectionKey = dialogSection === 'classes' ? 'fractions' : 
    //                       dialogSection === 'mapping' ? 'mappings' : 
    //                       dialogSection;
        
    //     newStates[sectionKey] = {
    //       hasValue: true,
    //       isConsistent: true,
    //       value: selectedValue
    //     };

    //     if (dialogSection === 'classes') {
    //       setCheckedSections(prev => ({
    //         ...prev,
    //         classes: true,
    //         fraction_displayName: false,
    //         fractionIcon: false,
    //         fractionArrow: false,
    //         mapping: false
    //       }));
    //       setLastKnownFractionStates({
    //         displayName: false,
    //         icon: false,
    //         arrow: false
    //       });
    //       setLastKnownMappingState(false);

    //       newStates.mappings = {
    //         hasValue: true,
    //         isConsistent: false,
    //         value: null
    //       };
    //       newStates.fractions = {
    //         hasValue: true,
    //         isConsistent: false,
    //         value: null
    //       };
    //     } else if (dialogSection.startsWith('fraction') || dialogSection === 'mapping') {
    //       newStates[sectionKey] = {
    //         hasValue: true,
    //         isConsistent: true,
    //         value: selectedValue
    //       };
    //     }

    //     return newStates;
    //   });
    // }

    console.log("consistencyDialogSelectedOptionIndex", consistencyDialogSelectedOptionIndex)
    const selectedOption = consistencyDialogOptions[consistencyDialogSelectedOptionIndex];
    const selectedOptionValue = selectedOption.value;

    var deploymentConfigNewValue = {...deploymentConfig};
    deploymentConfigNewValue[dialogSection] = selectedOptionValue;
    console.log("deploymentConfigNewValue", deploymentConfigNewValue)
    setDeploymentConfig(deploymentConfigNewValue);

    handleDialogClose();
  };

  function handleDialogCancel() {
    for (const section in sectionCheckedStateForSection) {
      const sectionCheckedState = sectionCheckedStateForSection[section];
      if (sectionCheckedState == DeploymentConfigSectionCheckedState.PROPOSED_CHECKED) {
        sectionCheckedStateForSection[section] = DeploymentConfigSectionCheckedState.UNCHECKED;
      }
    }

    handleDialogClose();
  }

  // Add function to format value for display
  const formatValueForDisplay = (value, section) => {
    if (!value) return 'No value';
    
    
    switch (section) {
      case 'model':
        return value.category + " - " + value.model;
      case "classes":
        var label = ""
        for (const fraction of value.fractions) {
          label += fraction.key + " (" + fraction.displayName + ")\n"
        }
        return label;
      case 'mappings':
        return Object.entries(value).map(([k, v]) => `${k} → ${v}`).join(', ');
      case 'display':
        return `Upper: ${value.upperLine}, Lower: ${value.lowerLine}`;
      case 'fraction_displayName':
        return value.join(", ");
      default:
        return JSON.stringify(value);
    }
  };

  // Add function to compare values
  const compareValues = (value1, value2) => {
    if (typeof value1 !== typeof value2) return false;
    
    if (typeof value1 === 'object' && value1 !== null) {
      // Handle arrays
      if (Array.isArray(value1)) {
        // For arrays, compare as sets (order-independent)
        if (value1.length !== value2.length) return false;
        
        // If the arrays contain objects (like fractions), compare by key
        if (value1.length > 0 && typeof value1[0] === 'object') {
          const keys1 = value1.map(item => item.key).sort();
          const keys2 = value2.map(item => item.key).sort();
          if (keys1.length !== keys2.length) return false;
          
          // Compare each item by key and properties
          return keys1.every(key => {
            const item1 = value1.find(item => item.key === key);
            const item2 = value2.find(item => item.key === key);
            return compareValues(item1, item2);
          });
        }
        
        // For simple arrays, compare as sets
        return value1.every(item => value2.includes(item)) &&
               value2.every(item => value1.includes(item));
      }
      
      // Handle dictionaries
      const keys1 = Object.keys(value1).sort();
      const keys2 = Object.keys(value2).sort();
      
      if (keys1.length !== keys2.length) return false;
      
      return keys1.every(key => {
        return compareValues(value1[key], value2[key]);
      });
    }
    
    return value1 === value2;
  };

  // Update checkSectionConsistency to handle mappings
  const checkSectionConsistency = (units, section) => {
    if (units.length === 0) return { hasValue: false, isConsistent: true };
    
    const values = units.map(unit => {
      const config = unit.deploymentConfig;
      if (!config) return null;
      
      switch (section) {
        case 'model':
          return {
            model: config.model,
            category: config.category,
            model_name: config.model_name,
            model_architecture: config.model_architecture,
            loss_function: config.loss_function,
            inference: config.inference
          };
        case 'fractions':
          return config.fractions;
        case 'mappings':
          return config.mappings;
        case 'display':
          return config.display;
        default:
          return null;
      }
    }).filter(v => v !== null);

    if (values.length === 0) return { hasValue: false, isConsistent: true };
    
    const firstValue = values[0];
    var isConsistent = true;

    return {
      hasValue: true,
      isConsistent: true,
      value: isConsistent ? firstValue : null
    };
  };
  

  // Update useEffect to use lastKnownFractionStates
  useEffect(() => {
    if (selectedTargets.length === 0) {
      // Reset values when no units are selected
      setCurrentValues({
        category: "",
        model: '',
        fractions: [],
        mappings: {},
        display: {
          upperLine: '',
          lowerLine: ''
        }
      });

      setDeploymentConfigText("")
      setMetadata({})
      return;
    } else {
      const targetDisplayUnit = selectedTargets[selectedTargets.length - 1];
      const targetDeploymentConfig = targetDisplayUnit.deploymentConfig;
      var localDeploymentConfig = {...targetDeploymentConfig}
      const _metadata = targetDeploymentConfig.metadata ?? {version_id: 0}
      delete localDeploymentConfig.metadata
      setDeploymentConfigText(JSON.stringify(localDeploymentConfig, null, 2))
      setMetadata(_metadata)
    }

    // Check consistency for each section
    const modelState = checkSectionConsistency(selectedTargets, 'model');
    const fractionsState = checkSectionConsistency(selectedTargets, 'fractions');
    const displayNameState = checkSectionConsistency(selectedTargets, "display_name")
    const mappingsState = checkSectionConsistency(selectedTargets, 'mappings');
    const displayState = checkSectionConsistency(selectedTargets, 'display');


    // Update current values if consistent
    if (modelState.isConsistent) {
      setCurrentValues(prev => ({ ...prev, category: modelState.value.category, model: modelState.value.model }));
    }
    if (fractionsState.isConsistent) {
      setCurrentValues(prev => ({ ...prev, fractions: fractionsState.value }));
    }
    if (mappingsState.isConsistent) {
      setCurrentValues(prev => ({ ...prev, mappings: mappingsState.value }));
    }
    if (displayState.isConsistent) {
      setCurrentValues(prev => ({ ...prev, display: displayState.value }));
    }

    // Update form fields if values are consistent
    if (fractionsState.isConsistent && fractionsState.value) {
      // Convert array to the format expected by the UI
      setFractions(fractionsState.value);
    }

    if (mappingsState.isConsistent && mappingsState.value) {
      const mappingsArray = Object.entries(mappingsState.value).map(([source, target]) => ({
        source,
        target
      }));
      setMappings(mappingsArray);
    }

    if (displayState.isConsistent && displayState.value) {
      setUpperLine(displayState.value.upperLine);
      setLowerLine(displayState.value.lowerLine);
    }

    const newTargetDisplayUnit = selectedTargets[selectedTargets.length - 1];
    var newTargetConfig = newTargetDisplayUnit?.deploymentConfig;
    var deploymentConfigNewValue;
    if (selectedTargets.length == 0) {
      deploymentConfigNewValue = new DeploymentConfig();
    } else if (selectedTargets.length == 1) {
      deploymentConfigNewValue = newTargetConfig;
    } else {
      deploymentConfigNewValue = DeploymentConfig.deploymentConfigFromAddingNewInstance(deploymentConfig, newTargetConfig);
    }

    setDeploymentConfig(deploymentConfigNewValue);
 
    var selectedTargetsDeploymentConfigsNewValue = {}
    if (selectedTargets.length > 0) {
      for (const displayUnit of selectedTargets) {
        const targetUnitConfig = displayUnit.deploymentConfig;
        if (targetUnitConfig) {
          const targetUnitDeploymentConfig = new UnitDeploymentConfig(displayUnit.id, targetUnitConfig)
          selectedTargetsDeploymentConfigsNewValue[displayUnit.id] = targetUnitDeploymentConfig
        }
      }
    }

    setSelectedTargetsDeploymentConfigs(selectedTargetsDeploymentConfigsNewValue)

    // Update checkboxes based on section consistency, using lastKnownStates
    setCheckedSections(prev => {
      const newClassesState = fractionsState.isConsistent;
      return {
        model: modelState.isConsistent,
        classes: newClassesState,
        mapping: newClassesState ? (mappingsState.isConsistent ? lastKnownMappingState : false) : false,
        display: displayState.isConsistent,
        fraction_displayName: newClassesState ? (fractionsState.isConsistent ? lastKnownFractionStates.displayName : false) : false,
        fractionIcon: newClassesState ? (fractionsState.isConsistent ? lastKnownFractionStates.icon : false) : false,
        fractionArrow: newClassesState ? (fractionsState.isConsistent ? lastKnownFractionStates.arrow : false) : false
      };
    });

  }, [selectedTargets]);

  useEffect(() => {
    const useExposureAlgorithm = deploymentConfig?.useExposureAlgorithm;

    setDeploymentConfigSubsections(prev => {
      return prev.map(subsection => {
        if (subsection.id == "useExposureAlgorithm") {
          console.log("Updating subsections")
          console.log("useExposureAlgorithm", useExposureAlgorithm)
          return {
            ...subsection,  
            value: useExposureAlgorithm
          };
        }
        return subsection;
      });
    });

    console.log("Deployment config useeffect", deploymentConfig)
  }, [deploymentConfig])

  // Update handleRollOut to show progress
  const handleRollOut = async () => {
    try {
      // Get all selected units
      const unitsToUpdate = selectedTargets;
      setProgress({
        current: 0,
        total: unitsToUpdate.length,
        message: 'Starting deployment...',
        isProcessing: true
      });

      // Parse the edited deployment config
      console.log('Parsing deployment config text:', deploymentConfigText);
      let updatedDeploymentConfig;
      try {
        updatedDeploymentConfig = JSON.parse(deploymentConfigText);
      } catch (e) {
        console.error('Failed to parse deployment config JSON:', e);
        throw new Error('Invalid JSON in deployment config: ' + e.message);
      }
      
      console.log('Parsed deployment config:', updatedDeploymentConfig);
      
      const updatedMetadata = {
        ...metadata,
        version_id: (metadata.version_id || 0) + 1
      };
      updatedDeploymentConfig.metadata = updatedMetadata;

      // Update each unit's deployment config
      for (let i = 0; i < unitsToUpdate.length; i++) {
        const unit = unitsToUpdate[i];
        setProgress(prev => ({
          ...prev,
          current: i + 1,
          message: `Updating unit ${unit.label} (${i + 1}/${unitsToUpdate.length})`
        }));

        try {
          const isDisplayUnit = unit.id.includes('_');
          
          if (isDisplayUnit) {
            // Update display unit - split from the right since locationId may contain underscores
            const lastUnderscoreIndex = unit.id.lastIndexOf('_');
            const locationId = unit.id.substring(0, lastUnderscoreIndex);
            const index = parseInt(unit.id.substring(lastUnderscoreIndex + 1), 10);
            
            console.log('Updating display unit:', { unitId: unit.id, locationId, index, companyId: unit.companyId });
            
            // Get the location document using the stored company ID
            const locationPath = `companies/${unit.companyId}/locations/${locationId}`;
            const locationRef = doc(db, locationPath);
            
            console.log('Fetching location document from path:', locationPath);

            const locationDoc = await getDoc(locationRef);
            
            console.log('Location doc exists:', locationDoc.exists());
            
            if (locationDoc.exists()) {
              const locationData = locationDoc.data();
              const displayUnitsArray = [...(locationData.display_units || [])];
              
              console.log('Display units array length:', displayUnitsArray.length, 'Updating index:', index);
              
              if (displayUnitsArray[index]) {
                console.log('Before update:', displayUnitsArray[index].deployment_config);
                displayUnitsArray[index] = {
                  ...displayUnitsArray[index],
                  deployment_config: updatedDeploymentConfig
                };
                console.log('After update:', displayUnitsArray[index].deployment_config);
                
                // Update the location document with the modified display_units array
                console.log('Attempting to update location at path:', locationPath);
                console.log('Update data:', { display_units: displayUnitsArray });
                
                try {
                  await updateDoc(locationRef, {
                    display_units: displayUnitsArray
                  });
                  console.log('Location document updated successfully');
                } catch (updateError) {
                  console.error('Update failed with error:', updateError.code, updateError.message);
                  console.error('Full error:', updateError);
                  throw updateError;
                }
              } else {
                console.error('Display unit at index', index, 'does not exist');
              }
            } else {
              console.error('Location document not found:', locationId);
            }
          } else {
            // Update regular unit
            const unitRef = doc(db, 'units', unit.id);
            
            await updateDoc(unitRef, {
              deployment_config: updatedDeploymentConfig
            });
          }
        } catch (error) {
          console.error(`Error updating unit ${unit.label}:`, error);
          throw error;
        }
      }
      
      await loadUnits();

      // Reset progress
      setProgress(prev => ({ ...prev, isProcessing: false }));

      // Show success message
      setSnackbar({
        open: true,
        message: `Successfully updated deployment config for ${unitsToUpdate.length} unit${unitsToUpdate.length > 1 ? 's' : ''}`,
        severity: 'success'
      });
    } catch (error) {
      console.error("Error updating deployment config:", error);
      // Reset progress
      setProgress(prev => ({ ...prev, isProcessing: false }));
      // Show error message
      setSnackbar({
        open: true,
        message: 'Error updating deployment config. Please try again.',
        severity: 'error'
      });
    }
  };

  // Add function to get units using a specific value
  const getUnitsUsingValue = (value, section) => {
    console.log("getUnitsUsingValue", value, section);
    const output = selectedTargets.filter(unit => {
      const config = unit.deploymentConfig;
      const comparisonUnitSectionValue = getSectionValue(config, section);

      console.log("unit", unit.id);
      console.log("comparisonUnitSectionValue", comparisonUnitSectionValue, value);
      const equal = JSON.stringify(comparisonUnitSectionValue) === JSON.stringify(value)
      console.log("equal", equal);
      return equal;

    });

    console.log("output", output);

    return output;
  };

  const isFractionEqual = (fraction1, fraction2) => {
    return fraction1.key === fraction2.key &&
           fraction1.displayName === fraction2.displayName &&
           fraction1.image === fraction2.image &&
           fraction1.arrow === fraction2.arrow;
  };

  const areFractionsEqual = (fraction1, fraction2) => {
    var equal = true
    if (fraction1.length != fraction2.length) {
      equal = false;
    } else {
      let i = 0;
      while (i < fraction1.length && equal) {
        if (!isFractionEqual(fraction1[i], fraction2[i])) {
          equal = false;
        }
        i++;
      }
    }
    return equal;
  }

  const getMappingsArray = () => {
    if (
      deploymentConfig
      && deploymentConfig.classes
      && deploymentConfig.classes.mappings
    ) {
      return Object.entries(deploymentConfig.classes.mappings).map(([source, target]) => ({
        source,
        target
      }));
    }

    return [];
  }

  return (
    <div className="roll-out-page">
      <ModelProvider category={category}>
        <div className="roll-out-page__content">
          <div className="roll-out-page__header">
            <Container maxWidth="lg">
              <Box py={4} sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
                {/* Main content area with two columns */}
                <Box sx={{ display: 'flex', gap: '24px', flex: 1, marginBottom: '20px' }}>
                  {/* Left column - Lists */}
                  <Box sx={{ flex: 0.6, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {/* Units Selection Section */}
                    <Paper sx={{ 
                      padding: '4px 24px 24px 24px', 
                      backgroundColor: '#1C1C1C',
                      border: '1px solid #2B2B2B'
                    }}>
                      <Box mb={4}>
                        <h3 style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', color: 'white' }}>
                          Selected Units
                        </h3>
                        <Box display="flex" gap={1} minHeight="32px" alignItems="center" flexWrap="wrap">
                          {selectedTargets.filter(t => units.some(u => u.id === t.id)).length > 0 ? (
                            selectedTargets.filter(t => units.some(u => u.id === t.id)).map((target) => (
                              <Chip
                                key={target.id}
                                label={target.label}
                                onDelete={() => handleRemoveTarget(target)}
                                color="primary"
                                variant="outlined"
                              />
                            ))
                          ) : (
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ 
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#dddddd',
                                fontFamily: 'Montserrat'
                              }}
                            >
                              No units selected
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <h5 style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '16px', color: 'white', marginBottom: '8px' }}>
                        Units
                      </h5>
                      <Box 
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr',
                          gap: '8px',
                          backgroundColor: '#2B2B2B',
                          padding: '16px',
                          borderRadius: '8px',
                          border: '1px solid #3B3B3B'
                        }}
                      >
                        {loading ? (
                          <p style={{ color: 'white', fontFamily: 'Montserrat' }}>Loading units...</p>
                        ) : (
                          units.map((unit) => (
                            <Box key={unit.id} display="flex" alignItems="center">
                              <Checkbox 
                                size="small"
                                checked={selectedTargets.some(t => t.id === unit.id)}
                                onChange={() => handleToggleTarget(unit)}
                                sx={{
                                  color: 'white',
                                  '&.Mui-checked': {
                                    color: 'var(--primary-green-500)'
                                  },
                                  '&.Mui-disabled': {
                                    color: 'var(--gray-500)'
                                  }
                                }}
                              />
                              <Typography variant="body2" sx={{ color: 'white' }}>{unit.label}</Typography>
                            </Box>
                          ))
                        )}
                      </Box>
                    </Paper>

                    {/* Display Units Selection Section */}
                    <Paper sx={{ 
                      padding: '4px 24px 24px 24px', 
                      backgroundColor: '#1C1C1C',
                      border: '1px solid #2B2B2B'
                    }}>
                      <Box mb={4}>
                        <h3 style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', color: 'white' }}>
                          Selected Display Units
                        </h3>
                        <Box display="flex" gap={1} minHeight="32px" alignItems="center" flexWrap="wrap">
                          {selectedTargets.filter(t => displayUnits.some(u => u.id === t.id)).length > 0 ? (
                            selectedTargets.filter(t => displayUnits.some(u => u.id === t.id)).map((target) => (
                              <Chip
                                key={target.id}
                                label={target.label}
                                onDelete={() => handleRemoveTarget(target)}
                                color="primary"
                                variant="outlined"
                              />
                            ))
                          ) : (
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ 
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#dddddd',
                                fontFamily: 'Montserrat'
                              }}
                            >
                              No display units selected
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <h5 style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '16px', color: 'white', marginBottom: '8px' }}>
                        Display Units
                      </h5>
                      <Box 
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr',
                          gap: '8px',
                          backgroundColor: '#2B2B2B',
                          padding: '16px',
                          borderRadius: '8px',
                          border: '1px solid #3B3B3B'
                        }}
                      >
                        {loading ? (
                          <p style={{ color: 'white', fontFamily: 'Montserrat' }}>Loading display units...</p>
                        ) : (
                          displayUnits.map((displayUnit) => (
                            <Box key={displayUnit.id} display="flex" alignItems="center">
                              <Checkbox 
                                size="small"
                                checked={selectedTargets.some(t => t.id === displayUnit.id)}
                                onChange={() => handleToggleTarget(displayUnit)}
                                sx={{
                                  color: 'white',
                                  '&.Mui-checked': {
                                    color: 'var(--primary-green-500)'
                                  },
                                  '&.Mui-disabled': {
                                    color: 'var(--gray-500)'
                                  }
                                }}
                              />
                              <Typography variant="body2" sx={{ color: 'white' }}>{displayUnit.label}</Typography>
                            </Box>
                          ))
                        )}
                      </Box>
                    </Paper>
                  </Box>

                  {/* Right column - Configuration */}
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <Paper sx={{ 
                      padding: '24px', 
                      backgroundColor: '#1C1C1C',
                      border: '1px solid #2B2B2B',
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1
                    }}>
                      
                      <h3 style={{ 
                        color: 'white',
                        fontFamily: 'Montserrat',
                        fontWeight: 500,
                        fontSize: '20px',
                        marginBottom: '16px',
                        marginTop: 0
                      }}>
                        Configuration
                      </h3>
                      
                      <div className="deployment-config-input-container" style={{ flex: 1, display: 'flex' }}>
                        <textarea 
                          value={deploymentConfigText}
                          onChange={(e) => setDeploymentConfigText(e.target.value)}
                          style={{ flex: 1 }}
                        />
                      </div>
                    </Paper>
                  </Box>
                </Box>

                {/* Bottom bar with roll out button and progress */}
                <Box 
                  display="flex" 
                  justifyContent="space-between" 
                  alignItems="center"
                  mt={3} 
                  sx={{ 
                    marginTop: 'auto',
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: 'rgba(28, 28, 28, 0.8)',
                    padding: '16px',
                    zIndex: 1000
                  }}
                >
                  {progress.isProcessing ? (
                    <Box sx={{ width: '100%', mr: 2 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={(progress.current / progress.total) * 100}
                        sx={{
                          height: 10,
                          borderRadius: 5,
                          backgroundColor: 'var(--gray-700)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: 'var(--primary-green-500)'
                          }
                        }}
                      />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mt: 1,
                          fontFamily: 'Montserrat',
                          color: 'white'
                        }}
                      >
                        {progress.message}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ flex: 1 }} />
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleRollOut}
                    disabled={selectedTargets.length === 0 || progress.isProcessing}
                    sx={{
                      minWidth: '120px',
                      backgroundColor: 'var(--primary-green-500)',
                      fontFamily: 'Montserrat',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: 'var(--primary-green-600)'
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'var(--gray-700)'
                      }
                    }}
                  >
                    {progress.isProcessing ? 'Processing...' : 'Roll out'}
                  </Button>
                </Box>
              </Box>
            </Container>
          </div>
        </div>
      </ModelProvider>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ fontFamily: 'Montserrat' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Montserrat', fontWeight: 600 }}>
          Select Value for {dialogSection?.charAt(0).toUpperCase() + dialogSection?.slice(1)}
        </DialogTitle>
        <DialogContent>
          <RadioGroup
            value={consistencyDialogSelectedOptionIndex}
            onChange={(e) => {
              console.log("e.target.value", e.target.value)
              setConsistencyDialogSelectedOptionIndex(parseInt(e.target.value))
            }}
          >
            <List>
              {consistencyDialogOptions.map((option, index) => {
                return (
                  <ListItem key={index}>
                    <Radio 
                      value={index}
                      checked={consistencyDialogSelectedOptionIndex === index}
                    />
                    <ListItemText 
                      primary={option.name}
                      secondary={
                        <Box component="span">
                          <Box component="span" display="block" sx={{ fontFamily: 'Montserrat' }}>
                            Used in {option.unitIds.length} unit{option.unitIds.length > 1 ? 's' : ''}
                          </Box>
                          <Box component="span" display="block" sx={{ mt: 0.5, fontFamily: 'Montserrat' }}>
                            {option.unitIds.join(', ')}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogCancel}>Cancel</Button>
          <Button onClick={handleDialogConfirm} variant="contained" color="primary">
            Use Selected Value
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RollOutPage; 